/**
 * CSV Import Form
 *
 * Extracted CSV upload form for the Import dialog.
 * Handles file selection, drag-and-drop, and optional property selection.
 */

import React from 'react';
import { Flex, Text, Button, Select, type SelectOption } from '@chatsdet/ui';
import { UploadSimple, CheckCircle } from '@phosphor-icons/react';

type CsvImportFormProps = {
  propertyOptions: SelectOption[];
  showPropertySelect: boolean;
  onSubmit: (file: File, defaultPropertyId?: string | null) => void;
  isPending: boolean;
  onCancel: () => void;
};

export function CsvImportForm({
  propertyOptions,
  showPropertySelect,
  onSubmit,
  isPending,
  onCancel,
}: CsvImportFormProps) {
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploadPropertyId, setUploadPropertyId] = React.useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleDragOver = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
  }, []);

  const handleDrop = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      setUploadFile(file);
    }
  }, []);

  return (
    <>
      <Flex direction="column" gap="md">
        <div
          data-dropzone={!uploadFile && !isDraggingOver ? true : undefined}
          className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            uploadFile
              ? 'border-green-500 bg-green-500/10'
              : isDraggingOver
                ? 'border-primary bg-primary/10'
                : 'border-border bg-muted/30 hover:border-muted-foreground/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploadFile && fileInputRef.current?.click()}
        >
          {uploadFile ? (
            <Flex direction="column" align="center" gap="sm">
              <CheckCircle size={24} weight="fill" className="text-green-500" />
              <div>
                <Text className="font-medium truncate max-w-[250px]">{uploadFile.name}</Text>
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground underline mt-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUploadFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  Remove file
                </button>
              </div>
            </Flex>
          ) : (
            <Flex direction="column" align="center" gap="sm">
              <UploadSimple size={24} className={isDraggingOver ? 'text-primary' : 'text-muted-foreground'} />
              <div>
                <Text className="font-medium">Drop CSV file here</Text>
                <Text size="sm" className="text-muted-foreground">
                  or click to browse
                </Text>
              </div>
            </Flex>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="csv-file-input"
            className="sr-only"
            onChange={(event) => {
              const nextFile = event.target.files?.[0] ?? null;
              setUploadFile(nextFile);
            }}
          />
        </div>
        {showPropertySelect ? (
          <Select
            label="Default application (optional)"
            placeholder="Select a fallback application"
            items={propertyOptions}
            selectedKey={uploadPropertyId ?? undefined}
            onSelectionChange={(key) => setUploadPropertyId(key ? String(key) : null)}
          />
        ) : null}
        <Text className="text-sm text-muted-foreground">
          The importer will infer apps and roles from the CSV. The default property is only
          used when inference is inconclusive.
        </Text>
      </Flex>
      <Flex gap="sm" justify="end" className="mt-4">
        <Button variant="outline" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            if (uploadFile) {
              onSubmit(uploadFile, uploadPropertyId);
            }
          }}
          isDisabled={isPending || !uploadFile}
        >
          {isPending ? 'Uploading...' : 'Upload'}
        </Button>
      </Flex>
    </>
  );
}
